import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import WelcomeScreen from "./questionTypes/WelcomeScreen";
import OpenQuestion from "./questionTypes/OpenQuestion";
import SingleChoice from "./questionTypes/SingleChoice";
import MultipleChoice from "./questionTypes/MultipleChoice";
import SingleChoiceOther from "./questionTypes/SingleChoiceOther";
import MultipleChoiceOther from "./questionTypes/MultipleChoiceOther";
import ScaleQuestion from "./questionTypes/ScaleQuestion";
import TermsAndConditions from "./questionTypes/TermsAndConditions";
import EndScreen from "./questionTypes/EndScreen";
import "./SurveyQuestionRenderer.scss";
import "./SurveyQuestion.scss";

/**
 * Builds Yup validation schema based on question configuration
 */
const buildValidationSchema = (question, t) => {
  const shape = {};

  switch (question.type) {
    case "open":
      if (question.required) {
        shape[`question_${question.id}`] = Yup.string()
          .required(t("survey:validation.required"))
          .min(1, t("survey:validation.requiredOther"));
      } else {
        shape[`question_${question.id}`] = Yup.string();
      }
      break;

    case "single":
    case "single_other":
      if (question.required) {
        shape[`question_${question.id}`] = Yup.string().required(
          t("survey:validation.requiredOne")
        );
      } else {
        shape[`question_${question.id}`] = Yup.string();
      }
      // For single_other, validate the "other" text field if selected
      if (question.type === "single_other") {
        shape[`question_${question.id}_other_text`] = Yup.string().when(
          `question_${question.id}`,
          {
            is: "other",
            then: schema =>
              schema.required(t("survey:validation.requiredOther")).min(1),
            otherwise: schema => schema,
          }
        );
      }
      break;

    case "multiple":
    case "multiple_other":
      if (question.required) {
        shape[`question_${question.id}`] = Yup.array()
          .min(1, t("survey:validation.requiredSelections"))
          .required(t("survey:validation.required"));
      } else {
        shape[`question_${question.id}`] = Yup.array();
      }

      // Add min selections validation if specified
      if (question.min_selections && question.min_selections > 0) {
        shape[`question_${question.id}`] = shape[`question_${question.id}`].min(
          question.min_selections,
          `Proszę wybrać przynajmniej ${question.min_selections} ${
            question.min_selections === 1
              ? t("survey:validation.option")
              : question.min_selections <= 4
              ? t("survey:validation.options")
              : t("survey:validation.options_plural")
          }`
        );
      }

      // Add max selections validation if specified
      if (question.max_selections && question.max_selections > 0) {
        shape[`question_${question.id}`] = shape[`question_${question.id}`].max(
          question.max_selections,
          `Możesz wybrać maksymalnie ${question.max_selections} ${
            question.max_selections === 1
              ? t("survey:validation.option")
              : question.max_selections <= 4
              ? t("survey:validation.options")
              : t("survey:validation.options_plural")
          }`
        );
      }
      // For multiple_other, validate the "other" text field if selected
      if (question.type === "multiple_other") {
        shape[`question_${question.id}_other_text`] = Yup.string().when(
          `question_${question.id}`,
          {
            is: value => Array.isArray(value) && value.includes("other"),
            then: schema =>
              schema.required(t("survey:validation.requiredAnother")).min(1),
            otherwise: schema => schema,
          }
        );
      }
      break;

    case "scale":
      if (question.required) {
        shape[`question_${question.id}`] = Yup.number()
          .required(t("survey:validation.scale"))
          .min(1)
          .max(10);
      } else {
        shape[`question_${question.id}`] = Yup.number()
          .min(1)
          .max(10);
      }
      break;

    case "terms_and_conditions":
      // Validate each required term
      if (question.terms && Array.isArray(question.terms)) {
        question.terms.forEach((term, index) => {
          if (term.required) {
            shape[`question_${question.id}_term_${index}`] = Yup.boolean()
              .oneOf([true], t("survey:validation.requiredTerms"))
              .required(t("survey:validation.required"));
          } else {
            shape[`question_${question.id}_term_${index}`] = Yup.boolean();
          }
        });
      }
      break;

    case "welcome_screen":
    case "end_screen":
      // No validation needed for informational screens
      break;

    default:
      break;
  }

  return Yup.object().shape(shape);
};

/**
 * Builds initial values for Formik based on question type
 */
const buildInitialValues = question => {
  const values = {};

  switch (question.type) {
    case "open":
      values[`question_${question.id}`] = "";
      break;

    case "single":
    case "single_other":
      values[`question_${question.id}`] = "";
      if (question.type === "single_other") {
        values[`question_${question.id}_other_text`] = "";
      }
      break;

    case "multiple":
    case "multiple_other":
      values[`question_${question.id}`] = [];
      if (question.type === "multiple_other") {
        values[`question_${question.id}_other_text`] = "";
      }
      break;

    case "scale":
      values[`question_${question.id}`] = null;
      break;

    case "terms_and_conditions":
      if (question.terms && Array.isArray(question.terms)) {
        question.terms.forEach((term, index) => {
          values[`question_${question.id}_term_${index}`] = false;
        });
      }
      break;

    case "welcome_screen":
    case "end_screen":
      // No values needed for informational screens
      break;

    default:
      break;
  }

  return values;
};

/**
 * Renders the appropriate question component based on type
 */
const renderQuestionComponent = (question, formikProps) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = formikProps;

  const fieldName = `question_${question.id}`;
  const fieldError = errors[fieldName];

  const commonProps = {
    question,
    fieldName,
    value: values[fieldName],
    error: fieldError,
    setFieldValue,
    setFieldTouched,
  };

  switch (question.type) {
    case "welcome_screen":
      return <WelcomeScreen question={question} />;

    case "open":
      return <OpenQuestion {...commonProps} />;

    case "single":
      return <SingleChoice {...commonProps} />;

    case "multiple":
      return <MultipleChoice {...commonProps} />;

    case "single_other":
      return (
        <SingleChoiceOther
          {...commonProps}
          otherTextValue={values[`${fieldName}_other_text`]}
          otherTextError={errors[`${fieldName}_other_text`]}
        />
      );

    case "multiple_other":
      return (
        <MultipleChoiceOther
          {...commonProps}
          otherTextValue={values[`${fieldName}_other_text`]}
          otherTextError={errors[`${fieldName}_other_text`]}
        />
      );

    case "scale":
      return <ScaleQuestion {...commonProps} />;

    case "terms_and_conditions":
      return (
        <TermsAndConditions
          question={question}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
      );

    case "end_screen":
      return <EndScreen question={question} />;

    default:
      return (
        <div className="survey-question-renderer__unknown">
          <p>Unknown question type: {question.type}</p>
        </div>
      );
  }
};

/**
 * Main Survey Question Renderer Component
 * Handles form state, validation, and rendering of different question types
 */
const SurveyQuestionRenderer = ({
  question,
  onSubmit,
  onPrevious,
  onSkip,
  currentStep,
  totalSteps,
  isFirst,
  isLast,
  initialValues: providedInitialValues,
}) => {
  const { t } = useTranslation(["survey"]);
  const validationSchema = useMemo(() => buildValidationSchema(question, t), [
    question,
  ]);

  const initialValues = useMemo(() => {
    return providedInitialValues || buildInitialValues(question);
  }, [question, providedInitialValues]);

  const handleSubmit = (values, { setSubmitting }) => {
    onSubmit(values);
    setSubmitting(false);
  };

  // Questions that don't require form submission (informational only)
  const isInformationalOnly =
    question.type === "welcome_screen" || question.type === "end_screen";

  return (
    <div className="survey-complete">
      <div className="survey-question-renderer">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={false}
          validateOnMount={false}
          touched
        >
          {formikProps => (
            <Form className="survey-question-renderer__form">
              {/* Progress Bar - Hidden for informational screens */}
              {totalSteps > 0 &&
                !isInformationalOnly &&
                question.type !== "terms_and_conditions" && (
                  <div className="survey-question-renderer__header">
                    {/* Question Header */}
                    <div className="survey-question__header">
                      <h1 className="survey-question__title">
                        {t("survey:wizard.question")} {currentStep}/{totalSteps}
                      </h1>
                    </div>

                    <div className="survey-question__progress-container">
                      <div className="survey-question__progress">
                        {[...Array(totalSteps)].map((_, index) => (
                          <div
                            key={index}
                            className={`survey-question__progress-bar ${
                              index < currentStep
                                ? "survey-question__progress-bar--active"
                                : ""
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              {question.type === "terms_and_conditions" && (
                <div className="survey-question-renderer__header">
                  {/* Question Header */}
                  <div className="survey-question__header">
                    <h1 className="survey-question__title">
                      {t("survey:wizard.almostDone")}
                    </h1>
                  </div>
                </div>
              )}

              {/* Question Content */}
              <div className="survey-question-renderer__content">
                {renderQuestionComponent(question, formikProps)}
                {question.type === "welcome_screen" && (
                  <div className="welcome_screen__next">
                    <button
                      type="submit"
                      className="survey-question__button survey-question__button--primary welcome_screen__start-button"
                    >
                      <span className="survey-question__button-text">
                        {t("survey:wizard.start")}
                      </span>
                    </button>
                  </div>
                )}
                {question.type === "end_screen" && (
                  <div className="welcome_screen__next">
                    <button
                      type="submit"
                      className="survey-question__button survey-question__button--primary"
                    >
                      <span className="survey-question__button-text">
                        {t("survey:wizard.close")}
                      </span>
                    </button>
                  </div>
                )}
              </div>

              {/* Navigation Footer - Hidden for informational screens */}
              {!isInformationalOnly && (
                <div className="survey-question__footer">
                  {!isFirst &&
                  onPrevious &&
                  question.type !== "terms_and_conditions" ? (
                    <button
                      type="button"
                      className="survey-question__button survey-question__button--secondary survey-question__button--previous"
                      onClick={onPrevious}
                    >
                      <svg
                        className="survey-question__button-icon survey-question__button-icon--flip"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.225 14.325L13.55 10L9.225 5.675"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="survey-question__button-text">
                        {t("survey:wizard.previous")}
                      </span>
                    </button>
                  ) : (
                    <div />
                  )}

                  <div className="survey-question__footer-right">
                    {!question.required &&
                    onSkip &&
                    !isLast &&
                    question.type !== "terms_and_conditions" ? (
                      <button
                        type="button"
                        className="survey-question__skip-button"
                        onClick={onSkip}
                      >
                        {t("survey:wizard.skip")}
                      </button>
                    ) : (
                      <div />
                    )}
                    <button
                      type="submit"
                      className="survey-question__button survey-question__button--primary"
                      disabled={formikProps.isSubmitting}
                    >
                      <span className="survey-question__button-text">
                        {question.type === "terms_and_conditions"
                          ? t("survey:wizard.submit")
                          : t("survey:wizard.next")}
                      </span>
                      <svg
                        className="survey-question__button-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.225 14.325L13.55 10L9.225 5.675"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

SurveyQuestionRenderer.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.oneOf([
      "welcome_screen",
      "open",
      "single",
      "multiple",
      "single_other",
      "multiple_other",
      "scale",
      "terms_and_conditions",
      "end_screen",
    ]).isRequired,
    title: PropTypes.string,
    question: PropTypes.string,
    description: PropTypes.string,
    required: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.string),
    terms: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        pdf_link: PropTypes.string,
        required: PropTypes.bool,
      })
    ),
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  onSkip: PropTypes.func,
  currentStep: PropTypes.number,
  totalSteps: PropTypes.number,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  initialValues: PropTypes.object,
};

SurveyQuestionRenderer.defaultProps = {
  onPrevious: null,
  onSkip: null,
  currentStep: 1,
  totalSteps: 0,
  isFirst: false,
  isLast: false,
  initialValues: null,
};

export default SurveyQuestionRenderer;
export { buildValidationSchema, buildInitialValues };
